import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";

import ProfileImage, {
  type ProfileImageProps,
} from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { TooltipPortal } from "common/components/ui/Tooltip/Tooltip";
import type { WorkspaceAccount } from "common/types";

import type { JSX, ReactNode } from "react";

const accountsTagVariants = cva(
  "inline-flex items-center max-w-full gap-x-1 rounded-full py-0.5 pl-1 pr-2 text-nowrap [&_svg]:-mr-1",
  {
    variants: {
      size: {
        sm: "gap-x-1 text-button-xxs [&_svg]:size-4",
        md: "gap-x-1 text-button-xs [&_svg]:size-5",
        lg: "gap-x-1.5 text-button-xs [&_svg]:size-6",
      },
      variant: {
        purple: "bg-purple-50",
        disabled: "bg-black-100 text-black-400",
        warning: "bg-yellow-100 [&_svg]:text-yellow-500",
      },
    },
  },
);

export type AccountsTagVariantType = VariantProps<
  typeof accountsTagVariants
>["variant"];

interface AccountsTagProps extends VariantProps<typeof accountsTagVariants> {
  accounts: WorkspaceAccount[];
  isLoading: boolean;
  tooltipContent: ReactNode;
  children: ReactNode;
  rightComponent?: JSX.Element;
}

const profileImageSizes: Record<
  AccountsTagProps["size"],
  ProfileImageProps["size"]
> = {
  sm: "3xs",
  md: "2xs",
  lg: "xs",
};

const tagHeights: Record<AccountsTagProps["size"], string> = {
  sm: "h-5",
  md: "h-6",
  lg: "h-7",
};

const ringColors: Record<AccountsTagVariantType, string> = {
  purple: "ring-purple-50",
  disabled: "ring-black-100",
  warning: "ring-yellow-100",
};

export default function AccountsTag({
  accounts,
  variant,
  size,
  isLoading,
  tooltipContent,
  children,
  rightComponent,
}: AccountsTagProps) {
  if (isLoading) {
    return (
      <Skeleton className={clsx("flex w-24 rounded-full", tagHeights[size])} />
    );
  }

  let profilePicturesComponent: JSX.Element;

  if (accounts.length <= 1) {
    profilePicturesComponent = (
      <ProfileImage
        size={profileImageSizes[size]}
        className={clsx(
          "ring-1 -ml-1.5 first:ml-0",
          variant === "purple" ? "ring-purple-50" : "ring-black-100",
        )}
        src={accounts.length === 1 ? accounts[0].picture_url : undefined}
      />
    );
  } else {
    profilePicturesComponent = (
      <div className="flex shrink-0 flex-row">
        {accounts.slice(0, 3).map(({ id, picture_url: pictureUrl }) => (
          <ProfileImage
            key={id}
            size={profileImageSizes[size]}
            className={clsx("ring-1 -ml-1.5 first:ml-0", ringColors[variant])}
            src={pictureUrl}
          />
        ))}
      </div>
    );
  }

  const tagComponent = (
    <div
      className={clsx(tagHeights[size], accountsTagVariants({ size, variant }))}
    >
      {profilePicturesComponent}
      <span className="truncate">{children}</span>
      {rightComponent}
    </div>
  );

  if (tooltipContent) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{tagComponent}</TooltipTrigger>
        <TooltipPortal>
          {/*TODO: REMOVE THIS WHEN WORKER ACCOUNTS TAG IS FIXED*/}
          <TooltipContent className="overflow-scroll max-h-80">
            {tooltipContent}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    );
  }
  return tagComponent;
}
